import { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';

import { Button, MenuItem, OverflowMenu, useConfirm, ActionsMenu, SearchInput } from '@pletly/ui';
import { getName } from '@pletly/shared';

import { AssignCaregiverModal } from '../AssignCaregiverModal';
import { useArchiveMember } from '../useArchiveMember';
import { withTranslation } from '../../../../locales/hoc';
import { OrganisationMemberDataType, OrganisationMembersTable } from '../OrganisationMembersTable';
import ChevronDownIcon from './chevron-down-white.svg';
import { useUpdateCareRecipientMemberMutation } from '../../../member/new/fillInformation/__generated__/updateCareRecipientMember.mutation.generated';
import { PressableCell } from '../../../components/PressableCell';
import { useGoToProfile } from '../../../components/useGoToProfile';
import { UsersContainer } from '../UsersContainer';
import { Spacer } from '../Spacer';
import { useMemberSearch } from '../useMemberSearch';

type Props = {
	organisationUuid: string;
} & { data: OrganisationMemberDataType[] };

export const CareRecipientsTable = withTranslation(({ data, organisationUuid }: Props) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation();
	const { confirm } = useConfirm();
	const {
		value: isNewMenuOpened,
		setTrue: openNewMenu,
		setFalse: closeNewMenu,
	} = useBoolean(false);
	const goToProfile = useGoToProfile();
	const [updateCareRecipientMember] = useUpdateCareRecipientMemberMutation();
	const { searchInput, filteredData } = useMemberSearch({ data });

	const [memberForModal, setMemberForModal] = useState<OrganisationMemberDataType>();

	const closeAssignCaregiverModal = useCallback(() => {
		setMemberForModal(undefined);
	}, []);

	const { handleArchive } = useArchiveMember();

	const goToNewTemporaryAccess = useCallback(
		<Row extends { memberId: string }>(row: Row) =>
			navigate('NewTemporaryAccess', { organisationUuid, memberId: row.memberId }),
		[navigate, organisationUuid],
	);

	const blockUserFromLogin = useCallback(
		async (row: OrganisationMemberDataType) => {
			const confirmation = await confirm(
				t('confirmations.block-organisation-member-login.title'),
				t('confirmations.block-organisation-member-login.content', { name: getName(row) }),
			);
			if (!confirmation) {
				return;
			}

			return updateCareRecipientMember({
				variables: {
					data: {
						memberId: row.memberId,
						organisationUuid,
						blockLogin: true,
					},
				},
			});
		},
		[t, updateCareRecipientMember, confirm, organisationUuid],
	);

	const renderRowActions = useCallback(
		({ row }: { row: OrganisationMemberDataType }) => (
			<ActionsMenu
				actions={[
					{
						title: t('table.actions.view-profile'),
						onPress: () => goToProfile(row),
					},
					{
						title: t('table.actions.assign-caregiver'),
						onPress: () => setMemberForModal(row),
					},
					{
						title: t('table.actions.invite-family-member'),
						onPress: () =>
							navigate('InviteFamilyToConnect', {
								userUuid: row.uuid,
								fullNameOrUsername: getName(row),
							}),
					},
					{
						title: t('table.actions.temporary-access'),
						onPress: () => goToNewTemporaryAccess(row),
					},
					{
						title: t('table.actions.deny-pletly-access'),
						onPress: () => blockUserFromLogin(row),
					},
					{
						title: t('table.actions.archive'),
						onPress: () => handleArchive(row),
					},
				]}
			/>
		),
		[t, goToProfile, navigate, goToNewTemporaryAccess, blockUserFromLogin, handleArchive],
	);

	const columnsConfig = useMemo(
		() => [
			{
				name: t('table.header.name'),
				accessor: 'firstName' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => (
					<PressableCell text={getName(row)} onPress={() => goToProfile(row)} />
				),
			},
			{
				name: t('table.header.username'),
				accessor: 'username' as const,
			},
		],
		[t, goToProfile],
	);

	const renderAddNewButton = () => (
		<Button
			style={styles.newMemberButton}
			variant="filled"
			color="primary"
			onPress={openNewMenu}
			accessoryRight={<ChevronDownIcon />}
			// @ts-expect-error valid for web
			dataSet={{
				includeTransformsInMeasurments: true,
			}}
		>
			{t('add-new')}
		</Button>
	);

	return (
		<UsersContainer>
			<UsersContainer.Header>
				<SearchInput {...searchInput} />
				<Spacer />
				<OverflowMenu
					anchor={renderAddNewButton}
					visible={isNewMenuOpened}
					onBackdropPress={closeNewMenu}
				>
					<MenuItem
						title={t('create-new-care-recipient')}
						onPress={() => {
							closeNewMenu();
							navigate('NewOrganisationMember', { organisationUuid, type: 'careRecipient' });
						}}
					/>
					<MenuItem
						title={t('request-ownership')}
						onPress={() => {
							closeNewMenu();
							navigate('ImportOrganisationMember', { organisationUuid, type: 'careRecipient' });
						}}
					/>
				</OverflowMenu>
			</UsersContainer.Header>

			<OrganisationMembersTable
				data={filteredData}
				columnsConfig={columnsConfig}
				renderRowActions={renderRowActions}
				onRowPress={goToProfile}
			/>

			{memberForModal ? (
				<AssignCaregiverModal
					visible
					onClose={closeAssignCaregiverModal}
					organisationUuid={organisationUuid}
					careRecipientMember={memberForModal}
				/>
			) : null}
		</UsersContainer>
	);
});

const styles = StyleSheet.create({
	newMemberButton: {
		alignSelf: 'flex-end',
	},
});
